import { authMiddleware, authPath, authReducer } from '@entities/Authentication/rtk-query.ts';
import { availablePeriodsReducer } from '@entities/available-periods';
import { cartReducer } from '@entities/cart';
import {
  deliveryApiMiddleware,
  deliveryApiPath,
  deliveryApiReducer,
} from '@entities/cart/model/rtkQueries.ts';
import { clientsReducer } from '@entities/clients';
import {
  clientsApiMiddleware,
  clientsApiReducer,
  clientsApiReducerPath,
} from '@entities/clients/rtk-query';
import { commonReducer } from '@entities/common';
import {
  commonApiMiddleware,
  commonApiPath,
  commonApiReducer,
} from '@entities/common/commonQueries/index.ts';
import { consultationsReducer } from '@entities/consultations';
import { contactsReducer } from '@entities/contacts';
import {
  eventApiMiddleware,
  eventApiReducer,
  eventApiReducerPath,
} from '@entities/events/rtk-query';
import { exercisesReducer } from '@entities/exercise-video';
import { productReducer } from '@entities/products';
import { programsReducer } from '@entities/programs';
import { promoReducer } from '@entities/promo-video';
import { schedule } from '@entities/schedules';
import {
  scheduleAddressApiReducerPath,
  scheduleAddressMiddleware,
  scheduleAddressQueriesReducer,
} from '@entities/schedules/address/rtkQueries.ts';
import {
  scheduleServiceApiReducerPath,
  scheduleServiceMiddleware,
  scheduleServiceQueriesReducer,
} from '@entities/schedules/service/rtkQueries';
import {
  scheduleTermApiReducerPath,
  scheduleTermMiddleware,
  scheduleTermQueriesReducer,
} from '@entities/schedules/term/index.ts';
import { supportReducer } from '@entities/support';
import { configureStore, type Action, type ThunkAction } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';
import { Provider } from 'react-redux';
import { commonFeatures } from '@features/common';
import { programFeatureReducers } from '@features/programs';
import { shareProgramReducer } from '@features/programs/program-share/model';
import {
  deliveryApiReducerPath,
  deliveryMiddleware,
  deliveryQueriesReducer,
} from '@shared/api/deliveryService';
import { rtkQueryErrorLogger } from '@shared/config';
import { noteEditorReducer } from '@shared/ui/note-editor/model/nodeEditorSlice';
import { injectStore } from '@src/api/instances.ts';
import events from 'library/redux/events';
import modals from 'library/redux/modal';
import notifications from 'library/redux/notifications';
import programs from 'library/redux/programs';
import shop from 'library/redux/shop';
import statistics from 'library/redux/statistics';

enableMapSet();
export const store = configureStore({
  reducer: {
    common: commonReducer,
    commonFeatures: commonFeatures,
    support: supportReducer,
    modals,
    shop,
    cart: cartReducer,
    events,
    programs,
    shareProgramReducer,
    notifications,
    statistics,
    clients: clientsReducer,
    programsReducer,
    exercisesReducer,
    promoReducer,
    programFeatureReducers,
    consultations: consultationsReducer,
    availablePeriods: availablePeriodsReducer,
    schedule,
    product: productReducer,
    coachContacts: contactsReducer,
    noteEditor: noteEditorReducer,
    [deliveryApiReducerPath]: deliveryQueriesReducer,
    [scheduleServiceApiReducerPath]: scheduleServiceQueriesReducer,
    [scheduleAddressApiReducerPath]: scheduleAddressQueriesReducer,
    [scheduleTermApiReducerPath]: scheduleTermQueriesReducer,
    [eventApiReducerPath]: eventApiReducer,
    [clientsApiReducerPath]: clientsApiReducer,
    [commonApiPath]: commonApiReducer,
    [authPath]: authReducer,
    [deliveryApiPath]: deliveryApiReducer,
  },
  devTools: process.env.NODE_ENV !== 'production',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    })
      .concat(rtkQueryErrorLogger)
      .concat([
        deliveryMiddleware,
        scheduleServiceMiddleware,
        scheduleAddressMiddleware,
        scheduleTermMiddleware,
        eventApiMiddleware,
        clientsApiMiddleware,
        commonApiMiddleware,
        authMiddleware,
        deliveryApiMiddleware,
      ]),
});

declare global {
  type AppDispatch = typeof store.dispatch;
  type RootState = ReturnType<typeof store.getState>;
  type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
}

injectStore(store);
export type AppStore = typeof store;

export const Store: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Provider store={store}>{children}</Provider>
);
